import { Component, OnInit, OnDestroy, ViewChild, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSettingsValidation } from 'node_js/models/business/user-settings-validation';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { User } from 'node_js/models/tables/user';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { UserSettingsService } from 'src/app/services/user-settings.service';
import { presentLoading, presentSnackBar } from 'src/global';
import { UserSettingsInputsComponent } from '../../user-settings-inputs/user-settings-inputs.component';
import { UserComplete } from 'node_js/models/business/user-complete';
import { UserSettingsComplete } from 'node_js/models/business/user-settings-complete';
import { Platform } from '@ionic/angular';
import { AndroidBackPriority } from 'node_js/models/enums/android-back-priority';
import { LanguageService } from 'src/app/services/language.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent  implements OnInit, OnDestroy {

  tempUserSettingsComplete:UserSettingsComplete;
  validationErrors:UserSettingsValidation = {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "", 
      displayName: "",
      dob: "",
      phoneNumber: "",
      gender: ""
  }

  @ViewChild("userSettingsInputs") userSettingsInputs:UserSettingsInputsComponent;
  
  // Back button subscription
  private backButtonSub: any;

  forceMode: boolean = false;

  constructor(public dialogRef: MatDialogRef<UserSettingsComponent>, private userAuthService:UserAuthService, private userSettingsService:UserSettingsService,
    private matDialog:MatDialog,private matSnackBar:MatSnackBar, private platform:Platform, private languageService: LanguageService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.forceMode = data?.forceMode || false;
  }

  ngOnInit() {
    // Create a deep clone of the user data to ensure complete isolation
    this.tempUserSettingsComplete = this.createDeepClone(this.userAuthService.currentUserComplete);
    console.log("tempUserSettingsComplete", this.tempUserSettingsComplete);
    
    // Set up back button handler
    this.handleBackButton();
    
    // Handle dialog close events to ensure proper cleanup
    this.dialogRef.afterClosed().subscribe(() => {
      console.log('[UserSettings] Dialog closed, cleaning up...');
      this.cleanupBackButtonSubscription();
    });
  }

  /**
   * Create a deep clone of the user data to ensure complete isolation
   * This prevents changes in the form from affecting the main user object
   */
  private createDeepClone(userComplete: UserComplete): UserSettingsComplete {
    // Deep clone the user object with only the properties that actually exist
    const clonedUser:User = {
      id: userComplete.user.id,
      authUid: userComplete.user.authUid,
      email: userComplete.user.email,
      password: userComplete.user.password,
      firstName: userComplete.user.firstName,
      lastName: userComplete.user.lastName,
      createdAt: userComplete.user.createdAt,
      registeredAt: userComplete.user.registeredAt,
      friendCode: userComplete.user.friendCode,
      displayName: userComplete.user.displayName,
      profileIconId: userComplete.user.profileIconId,
      lastLanguageId: userComplete.user.lastLanguageId,
      siteRole: userComplete.user.siteRole
    };

    // Deep clone the user push settings
    const clonedUserPushSettings = {
      id: userComplete.userSettingsComplete.userPushSettings.id,
      userId: userComplete.userSettingsComplete.userPushSettings.userId,
      enabled: userComplete.userSettingsComplete.userPushSettings.enabled,
      componentValueUpdates: userComplete.userSettingsComplete.userPushSettings.componentValueUpdates
    };

    return {
      user: clonedUser,
      userPushSettings: clonedUserPushSettings
    };
  }

  /**
   * Check if any changes were made to the user data
   */
  private hasUnsavedChanges(): boolean {
    if (!this.tempUserSettingsComplete || !this.userAuthService.currentUserComplete) {
      return false;
    }
    
    const original = this.userAuthService.currentUserComplete;
    const temp = this.tempUserSettingsComplete;
    
    // Check user properties
    if (temp.user.firstName !== original.user.firstName ||
        temp.user.lastName !== original.user.lastName ||
        temp.user.displayName !== original.user.displayName ||
        temp.user.email !== original.user.email ||
        temp.user.lastLanguageId !== original.user.lastLanguageId) {
      return true;
    }
    
    // Check push settings
    if (temp.userPushSettings.enabled !== original.userSettingsComplete.userPushSettings.enabled ||
        temp.userPushSettings.componentValueUpdates !== original.userSettingsComplete.userPushSettings.componentValueUpdates) {
      return true;
    }
    
    return false;
  }

  exitComponentFunc(){
    // In force mode, don't allow closing without required fields
    if (this.forceMode) {
      if (!this.isProfileComplete()) {
        presentSnackBar(this.languageService.instant('Please fill in your first name and last name to continue.'), SnackBarColor.DANGER, this.matSnackBar);
        return;
      }
    }

    // Check if there are unsaved changes
    if (this.hasUnsavedChanges()) {
      // Show confirmation dialog
      if (confirm(this.languageService.instant('You have unsaved changes. Are you sure you want to cancel?'))) {
        this.resetToOriginalData();
        this.dialogRef.close();
      }
    } else {
      // No changes, close directly
      this.dialogRef.close();
    }
  }

  isProfileComplete(): boolean {
    const user = this.tempUserSettingsComplete?.user;
    return !!(user?.firstName?.trim() && user?.lastName?.trim());
  }

  /**
   * Reset the temporary data to the original values from the main user object
   * This ensures data consistency when canceling changes
   */
  private resetToOriginalData() {
    this.tempUserSettingsComplete = this.createDeepClone(this.userAuthService.currentUserComplete);
    console.log("Reset to original data:", this.tempUserSettingsComplete);
  }

  navigateToDeleteAccount() {
    this.dialogRef.close();
    this.router.navigate(['/delete-user-data']);
  }

  updateUserInformation(){
    const selectedLanguageId = this.tempUserSettingsComplete?.user?.lastLanguageId;
    let loadingRef = presentLoading(this.matDialog);
    this.userSettingsService.updateUserInformation(this.tempUserSettingsComplete,this.userAuthService).then(async (userSettingsComplete:UserSettingsComplete)=>{
      const mergedUserSettingsComplete: UserSettingsComplete = {
        ...userSettingsComplete,
        user: {
          ...userSettingsComplete.user,
          lastLanguageId: selectedLanguageId ?? userSettingsComplete.user.lastLanguageId,
        },
      };

      this.tempUserSettingsComplete = mergedUserSettingsComplete;
      this.userAuthService.currentUserComplete.user = mergedUserSettingsComplete.user;
      this.userAuthService.currentUserComplete.userSettingsComplete.userPushSettings = mergedUserSettingsComplete.userPushSettings;

      if (selectedLanguageId != null && selectedLanguageId !== this.languageService.activeLanguage?.id) {
        await this.languageService.setLanguage(selectedLanguageId);
      }

      presentSnackBar("Your settings have been updated",SnackBarColor.SUCCESS,this.matSnackBar);
      
      // In force mode, close the dialog after successful save if profile is complete
      if (this.forceMode && this.isProfileComplete()) {
        this.dialogRef.close();
      }
    }).catch(()=>{
      presentSnackBar(this.languageService.instant('generic.unexpected-error'),SnackBarColor.DANGER,this.matSnackBar);
    })
    .finally(()=>{
      loadingRef.close();
    })
  }

  /**
   * Handle back button press when component is loaded as a dialog
   */
  handleBackButton() {
    console.log('[UserSettings] handleBackButton called');
    
    if (this.platform && this.platform.backButton) {
      console.log('[UserSettings] Setting up back button handler with priority:', AndroidBackPriority.DIALOG);
      this.backButtonSub = this.platform.backButton.subscribeWithPriority(AndroidBackPriority.DIALOG, () => {
        console.log('[UserSettings] 🎯 BACK BUTTON PRESSED! Checking for unsaved changes...');
        // Use the same logic as exitComponentFunc for consistency
        this.exitComponentFunc();
      });
      console.log('[UserSettings] ✅ Back button handler set up successfully');
    } else {
      console.log('[UserSettings] Platform or backButton not available');
    }
  }

  /**
   * Clean up back button subscription when component is destroyed
   */
  ngOnDestroy() {
    console.log('[UserSettings] ngOnDestroy called, cleaning up back button subscription');
    this.cleanupBackButtonSubscription();
  }

  /**
   * Clean up back button subscription manually
   */
  private cleanupBackButtonSubscription() {
    if (this.backButtonSub) {
      try {
        this.backButtonSub.unsubscribe();
        console.log('[UserSettings] ✅ Back button subscription cleaned up');
      } catch (err) {
        console.warn('[UserSettings] Error cleaning up back button subscription:', err);
      }
      this.backButtonSub = null;
    }
  }

  /**
   * Public method to cleanup back button subscription before dialog closes
   */
  public cleanupBeforeClose() {
    console.log('[UserSettings] cleanupBeforeClose called');
    this.cleanupBackButtonSubscription();
  }
}
